// SB6-ANA-PROG-01 — bridge: the analysis surface's existing query /
// hydration / activation signals → the derived readiness store.
//
// Mounted ONCE in the main analysis window (never in a pop-out). It reads
// the signals AnalysisFeature already has in hand, derives the phase with
// the pure `deriveReadiness`, and writes the snapshot into
// `useAnalysisReadinessStore`. No new requests are issued here.
import { useEffect, useRef } from "react";
import {
  deriveReadiness,
  SHELL_SNAPSHOT,
  useAnalysisReadinessStore,
  type QuerySignal,
} from "../state/analysisReadinessStore.js";
import { useDataPointFilterStore } from "../state/dataPointFilterStore.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useFilterApplyState } from "../filterRevision.js";

/** Minimal slice of a TanStack query the bridge needs (settle + error). */
export interface QueryLike {
  isPending: boolean;
  isError: boolean;
}

function toSignal(q: QueryLike): QuerySignal {
  return { settled: !q.isPending, isError: q.isError };
}

export interface AnalysisReadinessBridgeArgs {
  inspectionId: string;
  detail: QueryLike;
  prefs: QueryLike;
  images: QueryLike;
  findings: QueryLike;
  defects: QueryLike;
  dataPoints: QueryLike;
  /** True when the resolved image list is empty (no DP can ever activate). */
  datasetEmpty: boolean;
}

/**
 * Derive + publish the readiness snapshot. Returns nothing — consumers read
 * the store. Resets the store on unmount so a re-open starts clean.
 */
export function useAnalysisReadinessBridge(
  args: AnalysisReadinessBridgeArgs,
): void {
  const {
    inspectionId,
    detail,
    prefs,
    images,
    findings,
    defects,
    dataPoints,
    datasetEmpty,
  } = args;

  const filterHydrated = useDataPointFilterStore((s) => s.hydrated);
  const { applying: filterApplying } = useFilterApplyState(inspectionId);
  const activeImageId = useImageActivationStore((s) => s.activeImageId);

  const setSnapshot = useAnalysisReadinessStore((s) => s.set);
  const resetSnapshot = useAnalysisReadinessStore((s) => s.reset);

  // Install the not-ready shell baseline SYNCHRONOUSLY on first render so the
  // grading gate engages and the banner shows from the very first paint
  // (rather than the optimistic default lingering for one tick). The real
  // derived phase is written by the effect below on the same commit.
  const installedBaseline = useRef(false);
  if (!installedBaseline.current) {
    installedBaseline.current = true;
    useAnalysisReadinessStore.setState(SHELL_SNAPSHOT);
  }

  // Flatten the derived snapshot's primitive fields into the dependency
  // array so the effect runs whenever any input changes (objects are
  // re-created each render, so we pass primitives, not the query objects).
  const snapshot = deriveReadiness({
    detail: toSignal(detail),
    prefs: toSignal(prefs),
    images: toSignal(images),
    findings: toSignal(findings),
    defects: toSignal(defects),
    dataPoints: toSignal(dataPoints),
    filterHydrated,
    filterApplying,
    hasActiveImage: activeImageId !== null,
    datasetEmpty,
  });

  useEffect(() => {
    setSnapshot(snapshot);
  }, [
    setSnapshot,
    snapshot.phase,
    snapshot.shellReady,
    snapshot.coreDataReady,
    snapshot.filterApplied,
    snapshot.dataPointReady,
    snapshot.isReady,
    snapshot.failedStep,
  ]);

  useEffect(() => {
    return () => {
      resetSnapshot();
    };
  }, [resetSnapshot, inspectionId]);
}
